Woolman.Utils.prepareQueryParams();
Woolman.Collection = document.querySelector('[data-ajax-parent]');

const collectionRequestState = {
  sequence: 0,
  controller: null,
};

const setCollectionProcessing = (processing) => {
  Woolman.Collection?.classList?.toggle('processing', processing);
};

const isAbortError = (error) => error?.name === 'AbortError' || error?.code === 20;

const navigateToCollectionQuery = (query) => {
  location.search = query ? `?${query}` : '';
};

/**
 * Generates search params from form data for filtering collection or search results.
 * @param {HTMLElement} formElement - Form element to turn into search params.
 * @returns
 */
const generateSearchParamsFromFormData = (formElement) => {
  const checkboxesToKeep = {};
  for (const element of formElement.elements) {
    const key = element.getAttribute('name');
    if (!key || !key.includes('filter')) continue;

    const value = element.value;
    const inputType = element.getAttribute('type');

    if (value === '') continue;

    if (inputType == 'number' && (element.hasAttribute('data-min') || element.hasAttribute('data-max'))) {
      const priceSlider = element.closest('price-range-slider');
      if (!priceSlider || !priceSlider.hasAttribute('data-updated')) {
        continue; // Skip if slider hasn't been updated by user
      }
    }

    if (inputType == 'checkbox') {
      if (element.checked) {
        if (!checkboxesToKeep[key]) {
          checkboxesToKeep[key] = [value];
          continue;
        } else {
          checkboxesToKeep[key].push(value);
          continue;
        }
      }

      if (Shopify.queryParams[key] && !checkboxesToKeep[key]) {
        delete Shopify.queryParams[key];
      }
      continue;
    } else {
      Shopify.queryParams[key] = value;
    }
  }

  if (Object.entries(checkboxesToKeep).length > 0) {
    for (const [key, value] of Object.entries(checkboxesToKeep)) {
      Shopify.queryParams[key] = value.join(',');
    }
  }

  return Shopify.queryParams;
};

/**
 * Syncs checkbox states between all facets-form instances
 * Ensures sidebar and drawer forms stay in sync
 * @param {HTMLElement} changedCheckbox - The checkbox that was changed (optional)
 */
const syncCheckboxStates = (changedCheckbox = null) => {
  const allFacetsForms = document.querySelectorAll('facets-form');

  const findCheckbox = (form, name, value) => [...form.querySelectorAll('input[type="checkbox"]')].find(
    (checkbox) => checkbox.name === name && checkbox.value === value
  );

  // If a specific checkbox changed, sync only that one
  if (changedCheckbox) {
    const name = changedCheckbox.getAttribute('name');
    const value = changedCheckbox.value;
    const checked = changedCheckbox.checked;

    if (name && name.includes('filter')) {
      allFacetsForms.forEach(form => {
        const correspondingInput = findCheckbox(form, name, value);
        if (correspondingInput && correspondingInput !== changedCheckbox) {
          correspondingInput.checked = checked;
        }
      });
    }
    return;
  }

  // Otherwise, sync all checkboxes
  const allCheckboxes = document.querySelectorAll('facets-form input[type="checkbox"]');
  allCheckboxes.forEach(checkbox => {
    const name = checkbox.getAttribute('name');
    const value = checkbox.value;

    if (!name || !name.includes('filter')) return;

    // Find all corresponding checkboxes in other forms
    allFacetsForms.forEach(form => {
      const correspondingInput = findCheckbox(form, name, value);
      if (correspondingInput && correspondingInput !== checkbox) {
        correspondingInput.checked = checkbox.checked;
      }
    });
  });
};



/**
 * This function fetches fresh collection page via AJAX request to collection's url.
 * @param {Array} selectors - Array of HTML element selectors to update.
 */
const getCollectionAJAX = async (selectors) => {
  Shopify.queryParams.page = 1;
  const CollectionURLSearchParams = getCollectionURLSearchParams();
  const url = location.pathname + (CollectionURLSearchParams ? '?' + CollectionURLSearchParams : '');

  if (selectors) {
    collectionRequestState.controller?.abort();
    const requestSequence = ++collectionRequestState.sequence;
    const controller = new AbortController();
    collectionRequestState.controller = controller;
    setCollectionProcessing(true);

    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) {
        throw new Error(`Collection request failed with status ${res.status}`);
      }
      const body = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(body, 'text/html');

      if (requestSequence !== collectionRequestState.sequence) return;

      const fetchedGrid = doc.querySelector('#product-grid-ajax');
      const currentGrid = document.querySelector('#product-grid-ajax');
      if (!fetchedGrid || !currentGrid) {
        throw new Error('Collection response did not contain a product grid');
      }

      const replacements = selectors.map((selector) => ({
        selector,
        currentElement: document.querySelector(selector),
        fetchedElement: doc.querySelector(selector),
      }));
      if (replacements.some(({ currentElement, fetchedElement }) => currentElement && !fetchedElement)) {
        throw new Error('Collection response did not contain all existing replacement sections');
      }

      for (const { selector, currentElement, fetchedElement } of replacements) {
        if (!currentElement || !fetchedElement) continue;
        currentElement.innerHTML = fetchedElement.innerHTML;

        // Update apply number
        if (selector === '#product-grid-ajax') {
          let filteredCount = parseInt(fetchedElement.dataset.filteredCount, 10),
              context = fetchedElement.dataset.context,
              zeroResultsMsg = document.getElementById('facets-no-results-message'),
              resultsStringOne = window.themeStrings?.product_count_simple_one,
              resultsStringOther = window.themeStrings?.product_count_simple_other;

          if (context === 'search') {
            resultsStringOne = window.themeStrings?.results_with_count_one;
            resultsStringOther = window.themeStrings?.results_with_count_other;
          }

          const applyCount = document.getElementById('apply-count');
          const resultString = filteredCount === 1 ? resultsStringOne : resultsStringOther;
          if (applyCount && resultString && !Number.isNaN(filteredCount)) {
            applyCount.textContent = resultString.replace('[count]', filteredCount);
          }
          if (zeroResultsMsg) {
            zeroResultsMsg.classList.toggle('hide', filteredCount !== 0);
          }
        }
      }

      // Announce the new result count + move focus to a stable element so keyboard/SR users
      // are not stranded after a filter, sort, or chip change. WCAG 4.1.2 / 2.4.3.
      try {
        const _grid = doc.querySelector('#product-grid-ajax');
        const _fc = _grid ? parseInt(_grid.dataset.filteredCount, 10) : NaN;
        let _live = document.getElementById('facet-results-status');
        if (!_live) {
          _live = document.createElement('p');
          _live.id = 'facet-results-status';
          _live.className = 'visually-hidden';
          _live.setAttribute('role', 'status');
          _live.setAttribute('aria-live', 'polite');
          _live.setAttribute('tabindex', '-1');
          (Woolman.Collection || document.body).prepend(_live);
        }
        if (!Number.isNaN(_fc)) {
          const _tmpl = _fc === 1 ? window.themeStrings?.product_count_simple_one : window.themeStrings?.product_count_simple_other;
          _live.textContent = (_tmpl || '[count] products').replace('[count]', _fc);
        }
        _live.focus({ preventScroll: true });
      } catch (e) {}

      window.history.replaceState('', '', url);

    } catch (err) {
      if (!isAbortError(err) && requestSequence === collectionRequestState.sequence) {
        console.error('Collection filter request failed', err);
        navigateToCollectionQuery(CollectionURLSearchParams);
      }
    }
    finally {
      if (requestSequence === collectionRequestState.sequence) {
        collectionRequestState.controller = null;
        setCollectionProcessing(false);
      }
    }
  } else {
    navigateToCollectionQuery(CollectionURLSearchParams);
  }
};

/**
 * Returns URL for current collection. Utilizes URL parameters to save user's filters, sorting and pagination.
 * @returns
 */
const getCollectionURLSearchParams = () => {
  return new URLSearchParams(Object.entries(Shopify.queryParams)).toString().replaceAll(/%2C/g, ',');
};

class SortBySelect extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.select = this.querySelector('select');
    this.radios = this.querySelectorAll('input[type="radio"]');
    this.selectors = {
      grid: '#product-grid-ajax',
      pagination: '#pagination-ajax',
    };
    this.isSyncing = false; // Flag to prevent change events during sync

    if (Shopify.queryParams['sort_by']) {
      if (this.select) {
        this.select.value = Shopify.queryParams['sort_by'];
      } else if (this.radios.length > 0) {
        this.radios.forEach(radio => {
          if (radio.value === Shopify.queryParams['sort_by']) {
            radio.checked = true;
          }
        });
      }
    }

    if (this.select) {
      this.select.addEventListener('change', this.onSelectChange.bind(this));
    } else if (this.radios.length > 0) {
      this.radios.forEach(radio => {
        radio.addEventListener('change', this.onSelectChange.bind(this));
      });
    }
  }

  connectedCallback() {
    // Initial sync is handled by the constructor setting values from query params
  }

  /**
   * Syncs the value of this sort-by element to all other sort-by instances
   * @param {string} value - The value to sync to all instances
   */
  syncToAllInstances(value) {
    if (this.isSyncing) return; // Prevent infinite loops

    this.isSyncing = true;
    const allSortByElements = document.querySelectorAll('sort-by');

    allSortByElements.forEach((sortByElement) => {
      if (sortByElement === this) return; // Skip self

      sortByElement.isSyncing = true; // Set flag on target element

      // Handle select elements
      if (sortByElement.select) {
        if (sortByElement.select.value !== value) {
          sortByElement.select.value = value;
        }
      }
      // Handle radio buttons
      else if (sortByElement.radios.length > 0) {
        sortByElement.radios.forEach(radio => {
          radio.checked = (radio.value === value);
        });
      }

      sortByElement.isSyncing = false; // Clear flag on target element
    });

    this.isSyncing = false;
  }

  async onSelectChange(event) {
    // Don't trigger AJAX if this change was caused by syncing
    if (this.isSyncing) {
      return;
    }

    const value = event.target.value;

    // Sync to all other sort-by instances (without triggering their change events)
    this.syncToAllInstances(value);

    // Now trigger the AJAX update
    Woolman.Utils.prepareQueryParams();
    Shopify.queryParams.sort_by = value;
    getCollectionAJAX(Object.values(this.selectors));
  }
}

if (customElements.get('sort-by') === undefined) {
  customElements.define('sort-by', SortBySelect);
}

class FacetsForm extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this.querySelector('form');
    this.selectors = {
      grid: '#product-grid-ajax',
      count: '#products-count-ajax',
      currentFacets: '#current-facets-ajax',
      pagination: '#pagination-ajax',
    };
    this.listenersBound = false;

    if (this.el) {
      this.el.addEventListener('submit', this.onFormSubmit.bind(this));
    }
  }

  connectedCallback() {
    if (!this.el || this.listenersBound) return;
    this.listenersBound = true;
    // Set up input change listeners and sync functionality
    const inputs = this.el.querySelectorAll('input');

    inputs.forEach((input) => {
      // Create debounced AJAX handler for this input
      const debouncedAJAX = debounce(this.onInputChange.bind(this), 150);

      input.addEventListener('change', (event) => {
        if (input.type === 'checkbox') {
          // Sync immediately on change, before AJAX
          syncCheckboxStates(input);
        }
        // Then trigger debounced AJAX update
        debouncedAJAX(event);
      });
    });
  }

  async onFormSubmit(event) {
    event.preventDefault();
    Woolman.Utils.prepareQueryParams();
    generateSearchParamsFromFormData(this.el);

    const drawer = this.closest('[data-drawer]');
    if (drawer && Woolman.ModalsAndDrawers?.closeModalOrDrawer) {
      Woolman.ModalsAndDrawers.closeModalOrDrawer(drawer.getAttribute('id'));
    }
    getCollectionAJAX(Object.values(this.selectors));
  }

  async onInputChange(event) {
    generateSearchParamsFromFormData(this.el);
    getCollectionAJAX(Object.values(this.selectors));
  }
}

// Sync checkboxes when drawer opens
const drawerFilter = document.getElementById('drawer-filter');
if (drawerFilter) {
  const observer = new MutationObserver((mutations) => {
    if (drawerFilter.classList.contains('is-open')) {
      // Ensure query params are up to date
      Woolman.Utils.prepareQueryParams();

      // Sync all checkboxes from URL params
      const drawerForm = drawerFilter.querySelector('facets-form form');
      if (drawerForm) {
        const drawerCheckboxes = drawerForm.querySelectorAll('input[type="checkbox"]');
        drawerCheckboxes.forEach(checkbox => {
          const paramName = checkbox.getAttribute('name');
          const value = checkbox.value;

          if (paramName && paramName.includes('filter')) {
            const paramValues = Shopify.queryParams[paramName]?.split(',') || [];
            checkbox.checked = paramValues.includes(value);
          }
        });
      }

      // Sync all forms to ensure consistency
      syncCheckboxStates();
    }
  });

  observer.observe(drawerFilter, {
    attributes: true,
    attributeFilter: ['class']
  });
}

if (customElements.get('facets-form') === undefined) {
  customElements.define('facets-form', FacetsForm);
}

class CurrentFacetButton extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.selectors = {
      grid: '#product-grid-ajax',
      count: '#products-count-ajax',
      currentFacets: '#current-facets-ajax',
      pagination: '#pagination-ajax',
    };

    this.btn = this.querySelector('a');
    this.btn?.addEventListener('click', this.removeFacet.bind(this));
  }

  async removeFacet(event) {
    event.preventDefault();
    if (event.currentTarget.dataset.filterParamName === 'filter.v.price') {
      // Delete price filter params
      delete Shopify.queryParams['filter.v.price.lte'];
      delete Shopify.queryParams['filter.v.price.gte'];

      // Reset all price range sliders (sidebar and drawer)
      if (typeof PriceRangeSlider !== 'undefined' && PriceRangeSlider.resetAll) {
        PriceRangeSlider.resetAll();
      }

      // Remove data-updated attribute from all sliders
      const allSliders = document.querySelectorAll('price-range-slider');
      allSliders.forEach(slider => {
        slider.removeAttribute('data-updated');
      });

      getCollectionAJAX(Object.values(this.selectors));
    } else {
      const filterParamName = event.currentTarget.dataset.filterParamName;
      const filterValue = event.currentTarget.dataset.filterValue;
      const inputToReset = [...document.querySelectorAll('.filter-form [data-key-value]')].find(
        (input) => input.dataset.keyValue === `${filterParamName}:${filterValue}`
      );
      if (!inputToReset) return;
      inputToReset.checked = false;
      inputToReset.dispatchEvent(new Event('change'));
    }
  }
}
if (customElements.get('current-facet-button') === undefined) {
  customElements.define('current-facet-button', CurrentFacetButton);
}
